/**
 * Report Template Cards
 *
 * Displays available report templates for generating emission reports.
 */

import { useMemo } from 'react';
import { SquareCardGrid, type SquareCardItem } from './SquareCard';

interface ReportTemplateCardsProps {
  onSelectTemplate?: (templateId: string) => void;
  enabledIds?: string[];
}

const templates: Omit<SquareCardItem, 'comingSoon' | 'onClick'>[] = [
  {
    id: 'ghg-protocol',
    title: 'GHG Protocol Report',
    description: 'Comprehensive greenhouse gas inventory following GHG Protocol standards',
    icon: 'fa-file-lines',
    iconBgColor: 'rgba(40, 167, 69, 0.1)',
    iconColor: '#28a745',
  },
  {
    id: 'summary',
    title: 'Emissions Summary',
    description: 'Overview of emissions by scope, activity type, and time period',
    icon: 'fa-chart-pie',
    iconBgColor: 'rgba(0, 123, 255, 0.1)',
    iconColor: '#007bff',
  },
  {
    id: 'scope-detail',
    title: 'Scope Detail Report',
    description: 'Detailed breakdown by Scope 1 and Scope 2 emission sources',
    icon: 'fa-layer-group',
    iconBgColor: 'rgba(111, 66, 193, 0.1)',
    iconColor: '#6f42c1',
  },
  {
    id: 'assets',
    title: 'Assets Report',
    description: 'Emissions breakdown by buildings, fleets, and equipment in your asset register',
    icon: 'fa-building',
    iconBgColor: 'rgba(23, 162, 184, 0.1)',
    iconColor: '#17a2b8',
  },
  {
    id: 'custom',
    title: 'Custom Report',
    description: 'Build a custom report with selected metrics and time periods',
    icon: 'fa-sliders',
    iconBgColor: 'rgba(108, 117, 125, 0.1)',
    iconColor: '#6c757d',
  },
];

export const ReportTemplateCards = ({ onSelectTemplate, enabledIds = [] }: ReportTemplateCardsProps) => {
  const items: SquareCardItem[] = useMemo(
    () =>
      templates.map((t) => ({
        ...t,
        comingSoon: !enabledIds.includes(t.id),
        onClick: enabledIds.includes(t.id) ? () => onSelectTemplate?.(t.id) : undefined,
      })),
    [enabledIds, onSelectTemplate]
  );

  return (
    <div className='mb-4'>
      <h5 className='text-uppercase text-ThemeTextLight mb-3'>Report Templates</h5>
      <SquareCardGrid items={items} />
    </div>
  );
};
